/**
 * Crypto helpers basati su libsodium-wrappers (ESM, no CDN).
 *
 * Tutto avviene client-side. Le chiavi private non lasciano MAI il dispositivo.
 *
 * Esporta:
 *   - initSodium()                 — await prima di ogni op
 *   - keypairFromSeed(seed)        — Ed25519 da seed BIP39 (deterministica)
 *   - boxKeypairFromSeed(seed)     — X25519 da seed BIP39 (deterministica, via KDF)
 *   - deriveUid(publicKey)         — uid pubblico da Ed25519 pubkey
 *   - sign(msg, sec) / verify(sig, msg, pub)
 *   - boxEncrypt(msg, recipPub, senderSec) -> { ciphertext, nonce }
 *   - boxDecrypt(ct, nonce, senderPub, recipSec) -> string
 *   - toBase64Url(bytes) / fromBase64Url(str)
 */

import sodium from 'libsodium-wrappers';

let _ready = null;

/**
 * Aspetta che libsodium sia pronto. Da chiamare prima di ogni operazione crypto.
 */
export function initSodium() {
  if (_ready) return _ready;
  _ready = sodium.ready.then(() => sodium);
  return _ready;
}

/**
 * Deriva una keypair Ed25519 deterministica da un seed (32 byte).
 * Stesso seed → stessa keypair sempre.
 */
export async function keypairFromSeed(seedBytes) {
  const s = await initSodium();
  if (!(seedBytes instanceof Uint8Array) || seedBytes.length < 32) {
    throw new Error('SEED_INVALID');
  }
  const seed = seedBytes.slice(0, 32);
  const kp = s.crypto_sign_seed_keypair(seed);
  return {
    publicKey: kp.publicKey,   // Uint8Array(32)
    secretKey: kp.privateKey,  // Uint8Array(64)
  };
}

/**
 * Deriva un uid pubblico dalla publicKey.
 * uid = primi 22 char di base64url(blake2b-256(publicKey))
 * Identico tra client e server.
 */
export async function deriveUid(publicKey) {
  const s = await initSodium();
  const h = s.crypto_generichash(32, publicKey);
  return s
    .to_base64(h, s.base64_variants.URLSAFE_NO_PADDING)
    .slice(0, 22);
}

/**
 * Firma un messaggio con la chiave privata Ed25519.
 */
export async function sign(message, secretKey) {
  const s = await initSodium();
  const bytes = typeof message === 'string' ? s.from_string(message) : message;
  return s.crypto_sign_detached(bytes, secretKey);
}

/**
 * Verifica una firma Ed25519.
 */
export async function verify(signature, message, publicKey) {
  const s = await initSodium();
  const bytes = typeof message === 'string' ? s.from_string(message) : message;
  return s.crypto_sign_verify_detached(signature, bytes, publicKey);
}

/**
 * Conversione → base64url senza padding.
 */
export async function toBase64Url(bytes) {
  const s = await initSodium();
  return s.to_base64(bytes, s.base64_variants.URLSAFE_NO_PADDING);
}

/**
 * Conversione ← base64url senza padding.
 */
export async function fromBase64Url(str) {
  const s = await initSodium();
  return s.from_base64(str, s.base64_variants.URLSAFE_NO_PADDING);
}

// ─────────────────────────────────────────────────────────
// X25519 box (asymmetric encryption)
//
// Convenzione: deriviamo DUE keys da seed BIP39:
//   - Ed25519 signing keypair    (per firmare)
//   - X25519 box keypair         (per cifrare)
// Per derivare X25519 da seed deterministicamente: usiamo crypto_kdf_derive_from_key
// con un subkey-id dedicato. La master key è il seed.
// ─────────────────────────────────────────────────────────

const SUBKEY_CTX = 'NullumBx';  // 8-char context per kdf
const SUBKEY_ID_BOX = 1;

/**
 * Deriva una keypair X25519 (crypto_box) deterministica da un seed BIP39.
 * Usa crypto_kdf per derivare 32 byte di seed dedicato → poi crypto_box_seed_keypair.
 */
export async function boxKeypairFromSeed(seedBytes) {
  const s = await initSodium();
  if (!(seedBytes instanceof Uint8Array) || seedBytes.length < 32) {
    throw new Error('SEED_INVALID');
  }
  // master key per kdf: primi 32 byte del seed
  const masterKey = seedBytes.slice(0, 32);

  // Deriva un sub-key da 32 byte per inizializzare la box keypair
  const boxSeed = s.crypto_kdf_derive_from_key(
    32,
    SUBKEY_ID_BOX,
    SUBKEY_CTX,
    masterKey
  );

  const kp = s.crypto_box_seed_keypair(boxSeed);
  return {
    publicKey: kp.publicKey,   // 32 byte
    secretKey: kp.privateKey,  // 32 byte
  };
}

/**
 * Cifra un messaggio per un destinatario (X25519 + XSalsa20-Poly1305).
 * @param {string|Uint8Array} message
 * @param {Uint8Array} recipientPubKeyBox (32 byte)
 * @param {Uint8Array} senderSecKeyBox (32 byte)
 * @returns {{ciphertext: Uint8Array, nonce: Uint8Array}}
 */
export async function boxEncrypt(message, recipientPubKeyBox, senderSecKeyBox) {
  const s = await initSodium();
  const bytes = typeof message === 'string' ? s.from_string(message) : message;
  const nonce = s.randombytes_buf(s.crypto_box_NONCEBYTES);
  const ciphertext = s.crypto_box_easy(
    bytes, nonce, recipientPubKeyBox, senderSecKeyBox
  );
  return { ciphertext, nonce };
}

/**
 * Decifra un messaggio ricevuto.
 * @param {Uint8Array} ciphertext
 * @param {Uint8Array} nonce
 * @param {Uint8Array} senderPubKeyBox
 * @param {Uint8Array} recipientSecKeyBox
 * @returns {string} testo chiaro
 */
export async function boxDecrypt(ciphertext, nonce, senderPubKeyBox, recipientSecKeyBox) {
  const s = await initSodium();
  const plain = s.crypto_box_open_easy(
    ciphertext, nonce, senderPubKeyBox, recipientSecKeyBox
  );
  return s.to_string(plain);
}

/**
 * Sealed box ANONIMA (crypto_box_seal): cifra `message` verso la box-key del
 * destinatario usando una chiave EFFIMERA → il ciphertext NON contiene né rivela
 * il mittente. Solo il destinatario (con la sua secret key) può aprirla. Serve al
 * sealed-sender per nascondere `ikA` (identità del mittente) al server.
 * @returns {Promise<Uint8Array>}
 */
export async function boxSealAnon(message, recipientPubKeyBox) {
  const s = await initSodium();
  const pub = typeof recipientPubKeyBox === 'string' ? await fromBase64Url(recipientPubKeyBox) : recipientPubKeyBox;
  const bytes = typeof message === 'string' ? s.from_string(message) : message;
  return s.crypto_box_seal(bytes, pub);
}

/** Apre una sealed box anonima. Servono ENTRAMBE le chiavi box del destinatario. */
export async function boxSealOpen(ciphertext, recipientPubKeyBox, recipientSecKeyBox) {
  const s = await initSodium();
  const pub = typeof recipientPubKeyBox === 'string' ? await fromBase64Url(recipientPubKeyBox) : recipientPubKeyBox;
  const ct = typeof ciphertext === 'string' ? await fromBase64Url(ciphertext) : ciphertext;
  const plain = s.crypto_box_seal_open(ct, pub, recipientSecKeyBox);
  return s.to_string(plain);
}
